import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { CalendarBlank, Coins, ChartLine, IdentificationCard, PencilSimple, Briefcase, FloppyDisk, ShieldCheck } from "@phosphor-icons/react";
import ProfileSection from "@/components/ProfileSection";
import { AvailabilityCard } from "@/components/AvailabilityCard";
import TrailBreadcrumb from "@/components/TrailBreadcrumb";
import TrailBackPill from "@/components/TrailBackPill";
import ImageUploadField from "@/pages/admin/ImageUploadField";
import PageLoader from "@/components/PageLoader";

const PRO_TRAIL_SECTIONS = {
  bookings: { label: "Bookings", href: "/pro/bookings" },
  earnings: { label: "Earnings", href: "/pro/earnings" },
  profile: { label: "Profile", href: "/pro/profile" },
  therapists: { label: "Therapist directory", href: "/therapists" },
  mentors: { label: "Mentor directory", href: "/mentors" },
};

function fmtDate(d) {
  if (!d) return ",";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ProfessionalDashboard() {
  const { user, loading } = useAuth();
  const { format } = useCurrency();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ total_gross: 0, total_earnings: 0, count: 0 });
  const [edit, setEdit] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const [me, b] = await Promise.all([api.get("/pro/me"), api.get("/pro/bookings")]);
        if (!alive) return;
        setProfile(me.data);
        setBookings(b.data.bookings || []);
        setSummary(b.data.summary || {});
      } catch (e) {
        if (alive) setLoadError(e.response?.data?.detail || "Couldn't load your dashboard");
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (loading) return <Page><PageLoader /></Page>;
  if (!user) return <Navigate to="/login" replace />;
  if (loadError) {
    return (
      <Page>
        <div className="md-container md-section" data-testid="pro-dashboard-error">
          <h1 className="text-3xl font-serif mb-3">We couldn&apos;t load your professional profile</h1>
          <p style={{ color: "var(--md-text-soft)" }}>{loadError}</p>
          <p className="text-sm mt-2" style={{ color: "var(--md-text-muted)" }}>
            If this keeps happening, your professional profile may have been removed. Please <Link to="/contact" className="underline">contact us</Link> or re-apply from the Career page.
          </p>
        </div>
      </Page>
    );
  }
  if (user.role !== "professional") {
    return (
      <Page>
        <div className="md-container md-section" data-testid="pro-not-allowed">
          <h1 className="text-3xl font-serif mb-3">Professional area</h1>
          <p style={{ color: "var(--md-text-soft)" }}>
            This dashboard is for approved MindDhara professionals. If you&apos;ve applied and been approved, sign in with the credentials sent to your email.
          </p>
          <Link to="/login" className="btn-primary text-sm mt-5">Go to sign in</Link>
        </div>
      </Page>
    );
  }

  if (!profile) return <Page><PageLoader /></Page>;

  const startEdit = () => setEdit({
    title: profile.title || "",
    bio: profile.bio || "",
    photo_url: profile.photo_url || "",
    session_fee: profile.session_fee || 0,
    languages: (profile.languages || []).join(", "),
    specialisations: (profile.specialisations || []).join(", "),
    pathways: (profile.pathways || []).join(", "),
  });

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {
        title: edit.title,
        bio: edit.bio,
        photo_url: edit.photo_url,
        session_fee: Number(edit.session_fee) || 0,
        languages: edit.languages.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (profile.professional_type === "therapist") payload.specialisations = edit.specialisations.split(",").map((s) => s.trim()).filter(Boolean);
      else payload.pathways = edit.pathways.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/pro/me", payload);
      setProfile(data);
      setEdit(null);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally { setSavingProfile(false); }
  };

  const upcoming = bookings.filter((b) => ["confirmed", "paid", "pending_payment", "pending"].includes(b.status));
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.status));

  return (
    <Page>
      <Seo title="Professional Dashboard" description="Your MindDhara directory profile, bookings and earnings." />

      <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
        <div className="md-container">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <TrailBackPill
                scope="md.pro.trail"
                currentId="dashboard"
                sections={PRO_TRAIL_SECTIONS}
                className="mb-3"
              />
              <div className="md-label-eyebrow mb-2">Professional Portal · {profile.professional_type}</div>
              <h1 className="text-3xl md:text-4xl font-serif">Welcome, {profile.name.split(" ")[0]}</h1>
              <p className="text-base mt-1.5" style={{ color: "var(--md-text-soft)" }}>Manage your profile, see your bookings and your earnings.</p>
              <TrailBreadcrumb
                scope="md.pro.trail"
                current="My Dashboard"
                currentId="dashboard"
                homeLabel="Home"
                homeHref="/"
                sections={PRO_TRAIL_SECTIONS}
                className="mt-3"
              />
            </div>
            <Link to={`/${profile.professional_type === "therapist" ? "therapists" : "mentors"}/${profile.id}`} className="btn-outline text-sm" data-testid="pro-view-public">
              View public profile
            </Link>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="pro-summary-grid">
            <SummaryCard icon={CalendarBlank} label="Upcoming" value={upcoming.length} testid="pro-summary-upcoming" />
            <SummaryCard icon={Briefcase} label="All-time bookings" value={summary.count} testid="pro-summary-total" />
            <SummaryCard icon={Coins} label="Lifetime earnings" value={format(summary.total_earnings || 0)} testid="pro-summary-earnings" />
            <SummaryCard icon={ChartLine} label="Lifetime gross" value={format(summary.total_gross || 0)} testid="pro-summary-gross" />
          </div>
        </div>
      </section>

      {/* Profile + Bookings */}
      <section className="md-section">
        <div className="md-container grid lg:grid-cols-3 gap-6">
          {/* Profile card + availability */}
          <div className="lg:col-span-1 space-y-6">
            <div className="md-card" data-testid="pro-profile-card">
            <div className="flex items-center gap-3 mb-4">
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--md-bg-alt)" }}>
                  <IdentificationCard size={26} weight="duotone" style={{ color: "var(--md-primary)" }} />
                </div>
              )}
              <div>
                <h3 className="font-serif text-xl">{profile.name}</h3>
                <p className="text-xs" style={{ color: "var(--md-text-muted)" }}>{profile.title}</p>
              </div>
            </div>
            <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>{profile.bio}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <KeyVal k="Experience" v={`${profile.experience_years || 0} yrs`} />
              <KeyVal k="Session fee" v={format(profile.session_fee || 0)} />
              <KeyVal k="Languages" v={(profile.languages || []).join(", ") || ","} />
              <KeyVal k="Status" v={profile.approved ? "Approved" : "Pending"} />
            </div>
            <button onClick={startEdit} className="btn-outline text-sm mt-5 w-full" data-testid="pro-edit-btn">
              <PencilSimple size={14} weight="bold" /> Edit profile
            </button>
            </div>
            <AvailabilityCard profile={profile} onSaved={setProfile} />
          </div>

          {/* Upcoming bookings */}
          <div className="lg:col-span-2" data-testid="pro-bookings-section">
            <div className="md-label-eyebrow mb-3">Upcoming bookings</div>
            {upcoming.length === 0 ? (
              <div className="md-card text-center" style={{ padding: "2.5rem 1.5rem" }} data-testid="pro-no-bookings">
                <CalendarBlank size={32} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto" />
                <p className="mt-3 font-medium">No upcoming bookings yet.</p>
                <p className="text-sm mt-1" style={{ color: "var(--md-text-soft)" }}>
                  Your profile is live, clients book directly from the directory.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.map((b) => (
                  <div key={b.id} className="md-card flex items-center justify-between flex-wrap gap-3" data-testid={`pro-booking-${b.id}`}>
                    <div>
                      <div className="font-serif text-lg">{b.client_name}</div>
                      <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>{b.session_type || "Session"} · {fmtDate(b.date)} {b.time_slot}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>You earn</div>
                      <div className="text-lg font-medium" style={{ color: "var(--md-primary)" }}>{format(b.my_earnings || 0)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {past.length > 0 && (
              <>
                <div className="md-label-eyebrow mt-8 mb-3">Past sessions</div>
                <div className="md-card overflow-x-auto" data-testid="pro-past-table">
                  <table className="admin-table" style={{ minWidth: 540 }}>
                    <thead><tr><th>Date</th><th>Client</th><th>Gross</th><th>Your share</th><th>Status</th></tr></thead>
                    <tbody>
                      {past.map((b) => (
                        <tr key={b.id}>
                          <td>{fmtDate(b.date || b.created_at)}</td>
                          <td>{b.client_name}</td>
                          <td>{format(b.amount || 0)}</td>
                          <td style={{ color: "var(--md-primary)" }}>{format(b.my_earnings || 0)}</td>
                          <td style={{ textTransform: "capitalize" }}>{b.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Account profile (photo upload + password) */}
      <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
        <div className="md-container">
          <div className="md-label-eyebrow mb-1.5">Account</div>
          <h2 className="text-3xl font-serif mb-6">Your account</h2>
          <ProfileSection />
        </div>
      </section>

      {/* Edit modal (inline) */}
      {edit && (
        <div className="admin-modal-overlay" onClick={() => setEdit(null)} data-testid="pro-edit-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <header className="admin-modal-head">
              <div>
                <div className="md-label-eyebrow">Edit</div>
                <h2 className="admin-modal-title">Your directory profile</h2>
              </div>
              <button onClick={() => setEdit(null)} className="admin-modal-close" aria-label="Close">×</button>
            </header>
            <form onSubmit={saveProfile} className="admin-modal-body grid md:grid-cols-2 gap-4">
              <Field label="Title">
                <input className="md-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="pro-edit-title" />
              </Field>
              <Field label="Session fee (₹)">
                <input type="number" className="md-input" value={edit.session_fee} onChange={(e) => setEdit({ ...edit, session_fee: e.target.value })} data-testid="pro-edit-fee" />
              </Field>
              <Field label="Profile photo">
                <ImageUploadField
                  value={edit.photo_url}
                  onChange={(v) => setEdit({ ...edit, photo_url: v })}
                  endpoint="/uploads/public-image"
                  testid="pro-edit-photo"
                />
                <p className="text-xs mt-1" style={{ color: "var(--md-text-muted)" }} data-testid="hint-pro-photo">
                  Recommended: 800×800 px square (1:1), JPG/PNG under 500 KB
                </p>
              </Field>
              <Field label="Languages (comma)" cls="md:col-span-2">
                <input className="md-input" value={edit.languages} onChange={(e) => setEdit({ ...edit, languages: e.target.value })} data-testid="pro-edit-languages" />
              </Field>
              {profile.professional_type === "therapist" ? (
                <Field label="Specialisations (comma)" cls="md:col-span-2">
                  <input className="md-input" value={edit.specialisations} onChange={(e) => setEdit({ ...edit, specialisations: e.target.value })} data-testid="pro-edit-specialisations" />
                </Field>
              ) : (
                <Field label="Pathways (comma)" cls="md:col-span-2">
                  <input className="md-input" value={edit.pathways} onChange={(e) => setEdit({ ...edit, pathways: e.target.value })} data-testid="pro-edit-pathways" />
                </Field>
              )}
              <Field label="Bio" cls="md:col-span-2">
                <textarea className="md-input" rows={4} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} data-testid="pro-edit-bio" />
              </Field>
              <div className="md:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-outline text-sm">Cancel</button>
                <button type="submit" disabled={savingProfile} className="btn-primary text-sm" data-testid="pro-save-profile">
                  <FloppyDisk size={14} weight="bold" /> {savingProfile ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}

function SummaryCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="md-card" data-testid={testid}>
      <Icon size={22} weight="duotone" style={{ color: "var(--md-primary)" }} />
      <div className="text-2xl font-serif mt-2" style={{ color: "var(--md-primary)" }}>{value}</div>
      <div className="md-label-eyebrow mt-1">{label}</div>
    </div>
  );
}

function KeyVal({ k, v }) {
  return (
    <div>
      <div className="md-label-eyebrow">{k}</div>
      <div style={{ color: "var(--md-text)" }} className="text-sm font-medium mt-0.5">{v}</div>
    </div>
  );
}

function Field({ label, cls = "", children }) {
  return (
    <div className={cls}>
      <label className="md-label-eyebrow block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
